// Supported locales in the application
export const SUPPORTED_LOCALES = ['en', 'pl'] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Detect user's preferred language from browser settings
 * @returns The best matching supported locale or 'en' as fallback
 */
export function detectBrowserLanguage(): SupportedLocale {
  // Get browser languages in order of preference
  const browserLanguages = navigator.languages || [navigator.language || 'en'];

  for (const browserLang of browserLanguages) {
    // Extract the language code (e.g., 'en' from 'en-US')
    const langCode = browserLang.split('-')[0].toLowerCase();

    // Check if we support this language
    if (SUPPORTED_LOCALES.includes(langCode as SupportedLocale)) {
      return langCode as SupportedLocale;
    }
  }

  // Fallback to English if no supported language found
  return 'en';
}

/**
 * Check if a locale is supported by the application
 */
export function isSupportedLocale(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

/**
 * Extract locale from URL path
 * @param pathname - The URL pathname
 * @returns The locale if found in the path, undefined otherwise
 */
export function extractLocaleFromPath(
  pathname: string
): SupportedLocale | undefined {
  // Match locale only if it's followed by a slash or is the entire path
  // This prevents matching routes like /auth, /oauth-callback
  const localeMatch = pathname.match(/^\/([a-z]{2})(?:\/|$)/);
  const locale = localeMatch ? localeMatch[1] : undefined;

  // Only return if it's a supported locale
  return locale && isSupportedLocale(locale) ? locale : undefined;
}

/**
 * Convert locale to API format (en-US, pl-PL)
 * @param locale - The locale code ('en' or 'pl')
 * @returns The API-formatted locale string
 */
export function toApiLocale(locale: string): string {
  const langCode = locale.split('-')[0].toLowerCase();

  switch (langCode) {
    case 'pl':
      return 'pl-PL';
    case 'en':
    default:
      return 'en-US';
  }
}
